import { useApplication } from '@bonusly/application';

import { createHttpClient } from '../who-am-i/lib/http-client';

declare global {
	interface Window {
		Intercom?: {
			(action: 'boot', options: IntercomSettings): void;
			(action: 'update', options: Partial<IntercomSettings>): void;
			(action: 'trackEvent', event: string, data?: object): void;
			(action: 'startTour' | 'startChecklist', id: string): void;
			(action: 'showSpace' | 'hideSpace', space: string, callback?: () => void): void;
			(action: 'onShow', callback: () => void): void;
			(action: 'showNewMessage', message?: string): void;
			(action: 'show'): void;
		};
		intercomInitialized?: boolean;
	}
}

interface IntercomSettings {
	api_base: string;
	app_id: string;
	company: {
		id: string;
		name: string;
		is_superadmin_initialized: boolean;
		in_trial: boolean;
		product_tier: string;
	};
	created_at: number;
	email: string;
	finance_admin: boolean;
	global_admin: boolean;
	rewards_admin: boolean;
	tech_admin: boolean;
	name: string;
	user_hash: string;
	user_id: string;
	hide_default_launcher?: boolean;
}

interface Company {
	id: string;
	name: string;
	is_superadmin_initialized: boolean;
	product_tier: string;
	status: string;
}

interface User {
	id: string;
	created_at: string;
	email: string;
	display_name: string;
	finance_admin: boolean;
	global_admin: boolean;
	rewards_admin: boolean;
	tech_admin: boolean;
}

interface Config {
	company: Company;
	user: User;
	isMobile?: boolean;
}

export const initializeIntercom = async ({ company, user, isMobile }: Config) => {
	const { application } = useApplication.getState();
	const { intercomApiBase, intercomAppId, recognizeOrigin } = application;

	if (!window.Intercom || window.intercomInitialized || !intercomAppId) return;

	window.intercomInitialized = true;

	const http = createHttpClient(recognizeOrigin);
	const res = await http.get<{ hash: string }>('/api/v2/intercom_hash_generator');

	window.Intercom('boot', {
		api_base: intercomApiBase,
		app_id: intercomAppId,
		company: {
			id: company.id,
			name: company.name,
			is_superadmin_initialized: company.is_superadmin_initialized,
			in_trial: company.status === 'in_trial',
			product_tier: company.product_tier,
		},
		created_at: new Date(user.created_at).getTime() / 1000,
		email: user.email,
		name: user.display_name,
		user_hash: res.data.hash,
		user_id: user.id,
		finance_admin: user.finance_admin,
		global_admin: user.global_admin,
		rewards_admin: user.rewards_admin,
		tech_admin: user.tech_admin,
		hide_default_launcher: isMobile ?? false,
	});

	// show the tasks panel by default for trial users
	window.Intercom('onShow', () => {
		if (window.Intercom && company.status === 'in_trial') {
			window.Intercom('showSpace', 'tasks');
		}
	});
};

export const track = (event: string, data?: any) => {
	if (!window.Intercom) return;
	window.Intercom('trackEvent', event, data);
};

export const startTour = (id: string) => {
	if (!window.Intercom) return;
	window.Intercom('startTour', id);
};

export const startChecklist = (id: string) => {
	if (!window.Intercom) return;
	window.Intercom('startChecklist', id);
};

export const hideIntercom = () => {
	if (!window.Intercom) return;
	window.Intercom('update', { hide_default_launcher: true });
};

export const showIntercom = () => {
	if (!window.Intercom) return;
	window.Intercom('update', { hide_default_launcher: false });
};

export const showNewMessage = (message?: string) => {
	if (!window.Intercom) return;
	window.Intercom('showNewMessage', message);
};

// pass context to intercom as user attributes (visible to support, not to user)
export const updateContext = (context: Record<string, string | undefined>) => {
	if (!window.Intercom) return;
	// filter out undefined values
	const cleanContext = Object.fromEntries(Object.entries(context).filter(([, v]) => v !== undefined));
	if (Object.keys(cleanContext).length > 0) {
		window.Intercom('update', cleanContext);
	}
};

export const show = () => {
	if (!window.Intercom) return;
	window.Intercom('show');
};

// ============================================
// Intercom Checklists and Tours
// ============================================

export enum Checklists {
	OnboardingAdminTierOne = '42738459',
	OnboardingUserTierOne = '42738467',
	OnboardingAdminTierTwo = '40200126',
	OnboardingUserTierTwo = '41654411',
	OnboardingAdminTierAlpha = '52769267',
	OnboardingUserTierAlpha = '42738467',
	OnboardingAdmin = '40200126',
}

export enum Tours {
	RecognitionUser = '553832',
}
